import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link as MuiLink
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link, Navigate, useLocation } from 'react-router-dom';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import {
  DOCUMENT,
  documentPageCanvasSx,
  documentAuthPaperSx,
  documentAuthHeadingSx,
  documentAuthLogoSx,
  documentAuthFieldSx,
  documentAuthButtonSx,
  documentAuthLinkSx,
  documentAuthAlertSx,
} from '../constants/documentTheme';
import { isDeveloperHost, developerPath } from '../utils/developerHost';
import { hasPartnerSession, PARTNER_APP_HOME, partnerPostLoginPath } from '../utils/partnerWorkspace';

const PartnerLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/partners/login', {
        email,
        password
      });

      if (response.data.success) {
        localStorage.setItem('partnerToken', response.data.token);
        localStorage.setItem('partner', JSON.stringify(response.data.partner));

        const fromDeveloper = isDeveloperHost()
          || location.pathname.startsWith('/developer');
        navigate(
          fromDeveloper
            ? (location.state?.from?.pathname || developerPath())
            : partnerPostLoginPath(location.state?.from?.pathname),
          { replace: true }
        );
      }
    } catch (err) {
      console.error('Error logging in:', err);
      setError(err.response?.data?.error || 'Failed to log in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const onDeveloper = isDeveloperHost() || location.pathname.startsWith('/developer');

  if (!onDeveloper && hasPartnerSession()) {
    return <Navigate to={PARTNER_APP_HOME} replace />;
  }

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title={onDeveloper ? 'Developer Login - Déjà Vu Nomad' : 'Partner Login - Déjà Vu Nomad'}
        description="Access your Déjà Vu Nomad partner dashboard to manage packages, bookings, and inquiries."
        canonical={onDeveloper ? 'https://developer.dejavunomad.com/login' : 'https://dejavunomad.com/partner/login'}
        noIndex={true}
      />
      <DocumentMarketingHero
        eyebrow={onDeveloper ? 'Developers' : 'Partners'}
        title={onDeveloper ? 'Developer Login' : 'Partner Login'}
        subtitle={onDeveloper ? 'Sign in with your partner account' : 'Access your partner dashboard'}
      />
      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 } }}>
        <Paper elevation={0} sx={documentAuthPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/symbol.svg"
              alt=""
              sx={documentAuthLogoSx}
            />
            <Typography variant="h4" sx={documentAuthHeadingSx}>
              Sign In
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={documentAuthAlertSx}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              required
              disabled={loading}
              sx={{ ...documentAuthFieldSx, mt: 0 }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              required
              disabled={loading}
              sx={documentAuthFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={loading}
              sx={documentAuthButtonSx}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
                Don't have a partner account?{' '}
                <MuiLink component={Link} to="/partner/signup" sx={documentAuthLinkSx}>
                  Apply Here
                </MuiLink>
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" sx={{ color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
                Need help?{' '}
                <MuiLink href="mailto:info@dejavunomad.com" sx={documentAuthLinkSx}>
                  Contact Support
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PartnerLogin;
